import { Button } from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import {
  DataFields,
  FileUpload,
  UnprocessableFields,
} from 'api-clients/monolith';
import cx from 'classnames';
import React, { FormEvent, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { DuplicateApplicantModal } from 'components/DuplicateApplicantModal';
import { JobDescription } from 'components/JobDescription';
import { LoadingBoundary } from 'components/LoadingBoundary';
import { Opening } from 'components/Opening';
import { Snackbar } from 'components/Snackbar';
import { Typography } from 'components/Typography';
import {
  PreambleProps,
  ProgressManager,
  SubmitProps,
} from 'features/DataCollection/ProgressManager';
import { domainMeta } from 'utils/domainMeta';

import UserCircleIcon from '../../images/user-circle.svg';
import { DuplicateApplicantDataType } from './constants';
import { useStyles } from './styles';

import type {
  Opening as OpeningType,
  SubmitStatus,
} from './ApplicationFormBodyRoute';

export interface ApplicationFormBodyProps {
  ApplicationFormQuestionsComponent: React.ComponentType<PreambleProps>;
  canReapply: boolean;
  className?: string;
  duplicateApplicantData?: DuplicateApplicantDataType;
  handleDuplicateApplicantModalClose: () => void;
  handleReapply: () => Promise<void>;
  handleStartApplication: () => void;
  jobDescriptionHTML: string;
  opening: OpeningType;
  sendPreviousApplicationLink: () => Promise<void>;
  setSubmitting: React.Dispatch<React.SetStateAction<SubmitStatus>>;
  Submit: React.VFC<SubmitProps>;
  submitErrorMessage: (validation?: UnprocessableFields) => JSX.Element;
  submitting: SubmitStatus;
  handleSubmit: (
    formData: FormData,
    event?: FormEvent<Element>,
  ) => Promise<void>;
  dataFields: DataFields;
  formName: (dataKey: string) => string;
  uploadFields?: FileUpload;
  accountSlug: string;
  handleBackToTop: () => void;
  showAllJobsButton: boolean;
  hideJobWording: boolean;
  isDspBrand?: boolean;
  isItcDemoEnabled: boolean;
  showCompensation: boolean;
}

export const ApplicationFormBody: React.FC<ApplicationFormBodyProps> = ({
  ApplicationFormQuestionsComponent,
  canReapply,
  accountSlug,
  className,
  duplicateApplicantData,
  handleDuplicateApplicantModalClose,
  handleReapply,
  handleStartApplication,
  jobDescriptionHTML,
  opening,
  sendPreviousApplicationLink,
  setSubmitting,
  Submit,
  submitErrorMessage,
  submitting,
  handleSubmit,
  dataFields,
  formName,
  uploadFields,
  handleBackToTop,
  showAllJobsButton,
  hideJobWording,
  isDspBrand,
  isItcDemoEnabled,
  showCompensation,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const referrer = queryParams.get('referralWorkerName');

  const submitError = submitting.status === 'error';

  const jobTitle = useMemo(() => {
    if (isItcDemoEnabled)
      return intl.formatMessage({
        defaultMessage: 'Welcome',
        description: 'Job Description Section Header',
      });

    if (hideJobWording)
      return intl.formatMessage({
        defaultMessage: 'Description',
        description: 'Opening Description Section Header',
      });

    return intl.formatMessage({
      defaultMessage: 'Job Description',
      description: 'Job Description Section Header',
    });
  }, [hideJobWording, intl, isItcDemoEnabled]);

  return (
    <LoadingBoundary
      loading={submitting.status === 'loading'}
      className={className}
    >
      {duplicateApplicantData && (
        <DuplicateApplicantModal
          handleClose={handleDuplicateApplicantModalClose}
          {...duplicateApplicantData}
          handleCancel={canReapply ? handleReapply : undefined}
          handleSubmit={sendPreviousApplicationLink}
        />
      )}
      <div className={styles.columnLayout}>
        {referrer && (
          <div className={styles.referrerNotice}>
            <img alt="UserCircleIcon" src={UserCircleIcon} />
            <FormattedMessage
              defaultMessage="{referrer} referred you for this job."
              description="Name of worker who referred applicant to job."
              values={{ referrer }}
            />
          </div>
        )}
        <Snackbar
          open={submitError}
          severity="error"
          onClose={() => setSubmitting({ status: 'idle' })}
          message={submitErrorMessage(
            submitError ? submitting.error : undefined,
          )}
        />
        <Opening {...opening} showCompensation={showCompensation} />
        <Button
          className={styles.desktopSmallerButton}
          onClick={handleStartApplication}
          variant="contained"
          color="primary"
        >
          {isItcDemoEnabled ? (
            <FormattedMessage
              defaultMessage="Start Your Journey"
              description="Start Application button for the Application Form"
            />
          ) : (
            <FormattedMessage
              defaultMessage="Start Application"
              description="Start Application button for the Application Form"
            />
          )}
        </Button>
        {jobDescriptionHTML && (
          <JobDescription title={jobTitle} body={jobDescriptionHTML} />
        )}
        <div className={cx(styles.columnLayout, styles.desktopBorder)}>
          <Typography variant="headingMD">
            {isItcDemoEnabled ? (
              <FormattedMessage
                defaultMessage="Start Your Journey"
                description="Start Your Application Section Header"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Start Your Application"
                description="Start Your Application Section Header"
              />
            )}
          </Typography>

          <ProgressManager
            Preamble={ApplicationFormQuestionsComponent}
            Submit={Submit}
            onSubmit={handleSubmit}
            dataFields={dataFields}
            inputName={formName}
            uploadFields={uploadFields}
            countryCode={opening.countryCode}
            serverInvalidFields={submitError ? submitting.error : undefined}
          />
        </div>
        {showAllJobsButton && (
          <Button
            href={
              isDspBrand
                ? `${domainMeta.monolithOrigin}/jobs/${accountSlug}`
                : `${domainMeta.monolithOrigin}/c/${accountSlug}`
            }
            variant="outlined"
          >
            {hideJobWording ? (
              <FormattedMessage
                defaultMessage="View all"
                description="Link to view all openings"
              />
            ) : (
              <FormattedMessage
                defaultMessage="View all openings"
                description="Link to view all openings"
              />
            )}
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<ArrowUpwardIcon />}
          onClick={handleBackToTop}
        >
          <FormattedMessage
            defaultMessage="Back to top"
            description="Button to scroll to top of page"
          />
        </Button>
      </div>
    </LoadingBoundary>
  );
};
